import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { AssetGroupResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper } from '../utils';
import { ICONS } from '../icons';
import { AssetGroupDetailsSectionHandler } from './details.section';
import { ContactsSectionHandler } from '../shared/contacts.section';
import { AssetGroupingService } from 'app/services';
import { AssetListSectionHandler } from '../shared/list.section';
import { RegisterEntityHandler } from '../../entities';

@RegisterEntityHandler('assetgroup')
@Injectable()
export class AssetGroupHandler extends EntityHandler<AssetGroupResponse> {

    currentEntity: AssetGroupResponse;

    constructor(
        admin: AdminEditorService,
        private grouping: AssetGroupingService
    ) {
        super(admin);
        this.listMode = 'tree';
    }

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        // we can't paginate tree items, so hard code a limit of 250 and hope it's enough
        offset = 0;
        limit = 250;
        return this.admin.getCached(`listAssetGroups/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listAssetGroups(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'assetgroup',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.ASSETGROUPS'),
        }));
    }

    getEntity(id: string): Promise<AssetGroupResponse> {
        return this.admin.getCached(`getAssetGroup/${id}`, api => api.entities.getAssetGroup(id), 10).then(result => {
            this.currentEntity = result;
            return result;
        });
    }

    createEntity(ownerId: string, entity: any): Promise<AssetGroupResponse> {
        entity.ownerId = ownerId;
        this.grouping.clear(); // clear our grouping cache after an update
        return this.admin.applyUpdate(api => api.entities.createAssetGroup(entity));
    }


    setEntityState(entity: any, state: string): Promise<any> {
        if (state === 'deleted') {
            this.grouping.clear(); // clear our grouping cache after an update
            this.currentEntity = null;
            return this.admin.applyUpdate(api => api.entities.deleteAssetGroup(entity.id));
        }
        return Promise.reject('Only deletion supported');
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSETGROUP.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', (row) => {
                return row.folder ? ICONS.folder : ICONS.assetgroup;
            }),
            // grid.TextColumn('assets', 'ASSETS', 80), TODO: add back when the backend can return a count
            grid.TimeAgoColumn('entity.modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    getEditorSections(ownerId: string, _entity: any): Promise<SectionHandlerGroup<AssetGroupResponse>[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSET.COLUMNS');
        const { id, name } = this.currentEntity;
        return Promise.resolve([{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new AssetGroupDetailsSectionHandler(this.admin, ownerId, this.grouping),
                new ContactsSectionHandler(this.admin, ownerId, 'assetgroup'),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.ASSETS'),
            sections: [
                new AssetListSectionHandler(this.admin, ownerId, ['add', 'delete'], this.grouping, 'groups', [grid.IdNameArrayComboColumn('groups', 'GROUPS', [], 150)], { id, name }, async () => {
                    const result = await this.admin.showFormModal({
                        groups: [{
                            name: this.admin.i18n.instant('ADMIN.EDITORS.SHARED.LIST.REPLACE_ASSET_GROUP'),
                            fields: [{
                                type: 'searchselect', id: 'group', title: this.admin.i18n.instant('ADMIN.ENTITIES.ASSETGROUPS'), max: 1, required: true, options: {
                                    searchItemPromise: groupId => this.admin.getCached(`getAssetGroup/${groupId}`, api => api.entities.getAssetGroup(groupId)),
                                    searchListPromise: async (_limit, filter) => ({ items: filter ? this.grouping.getAssetGroups() : this.grouping.getAssetGroupsAsTree() } as any),
                                },
                            }],
                        }],
                    }, { group: null });

                    if (!result) { return null; }
                    const groups = await this.grouping.getAssetGroups();
                    const group = groups.find(x => x.id === result.group.id);
                    return { id: group.id, name: group.name };
                }),
            ],
        }]);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<AssetGroupResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'assetgroup',
            entity: {
                parent: this.currentEntity ? { id: this.currentEntity.id, name: this.currentEntity.name } : null,
            },
            sections: [
                new AssetGroupDetailsSectionHandler(this.admin, ownerId, this.grouping),
            ],
        });
    }

}













